import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, } from "react";
import { Button } from "../button";
import { KeyBoardKeys } from "../types";
import { handleLeadingZero, debounce, prepareChipsToSimulateLoop, } from "./utils";
import { SCROLL_DELAY, SCROLL_HEIGHT, TIME_CHIP_HEIGHT, CHIPS_SIZE, SCROLLBARS_INDEXES, ANIMATION_DURATION, } from "./constants";
import { useScrollAnimation } from "./useScrollAnimation";
import { Chip } from "./Chip";
const preventScroll = (e) => {
    e.preventDefault();
};
export function Chips({ unit, interval, onValueChange, format, onCloseRequest, }) {
    const scrollBarRef = useRef(null);
    const focusAreaRef = useRef(null);
    const { animateScroll } = useScrollAnimation(scrollBarRef);
    const scrollBarIndex = SCROLLBARS_INDEXES[unit];
    const isHoursColumn = scrollBarIndex === SCROLLBARS_INDEXES.h;
    const isAnteMeridiem = Number(format) === 12;
    const onClick = (event) => {
        const timeUnit = Number(event.currentTarget.getAttribute("data-chip-value"));
        if (!/\d/.test(String(timeUnit))) {
            return;
        }
        if (document.activeElement.tagName !== "INPUT" &&
            document.activeElement !== focusAreaRef.current)
            focusAreaRef.current.focus();
        const scrollTop = (timeUnit / interval) * TIME_CHIP_HEIGHT;
        if (event.currentTarget.hasAttribute("data-pre-chip-clone-index")) {
            if (event.currentTarget.getAttribute("data-pre-chip-clone-index") === "2") {
                animateScroll(0);
                const timeValue = parseInt(event.currentTarget.innerHTML, 10);
                if (typeof onValueChange === "function" &&
                    /\d/.test(String(timeValue))) {
                    let time = timeValue;
                    if (isHoursColumn) {
                        if (isAnteMeridiem && timeValue === 0) {
                            time = 12;
                        }
                        else if (timeValue === 24) {
                            time = 0;
                        }
                    }
                    else if (timeValue === 60) {
                        time = 0;
                    }
                    onValueChange(handleLeadingZero(time));
                }
                return;
            }
            const cb = () => animateScroll(scrollTop + TIME_CHIP_HEIGHT);
            const maxScrollTopValue = 2680;
            animateScroll(maxScrollTopValue, 0, cb);
            return;
        }
        if (event.currentTarget.hasAttribute("data-post-chip-clone-index")) {
            const cb = () => animateScroll(scrollTop + TIME_CHIP_HEIGHT);
            if (event.currentTarget.getAttribute("data-post-chip-clone-index") === "0") {
                animateScroll(scrollTop, 0, cb);
            }
            else {
                animateScroll(0, 0, cb);
            }
            return;
        }
        animateScroll(scrollTop + TIME_CHIP_HEIGHT);
    };
    const handleMeridian = (minute) => ({
        text: isAnteMeridiem && minute === 0 ? "12" : minute,
        value: minute,
    });
    const buildProps = ({ text, value }) => ({
        unit,
        scrollBarIndex,
        onClick,
        text,
        value,
    });
    const memoisedBuildProps = useCallback(buildProps, [unit, scrollBarIndex]);
    const unitsChips = useMemo(() => {
        const size = CHIPS_SIZE[unit];
        let count = size;
        if (format && isHoursColumn && Number(format) !== size) {
            count = Number(format);
        }
        return new Array(count)
            .fill(null)
            .map((_, i) => i)
            .filter((i) => i % interval === 0)
            .map(handleMeridian)
            .map(memoisedBuildProps)
            .map(Chip);
    }, [unit, format, interval]);
    const chips = useMemo(() => prepareChipsToSimulateLoop(unitsChips, _jsx(Button, { stylingType: "flat" })), [unitsChips]);
    const onScroll = (e) => {
        e.preventDefault();
        if (!scrollBarRef.current)
            return;
        const { scrollTop: Scrolltop, scrollHeight } = scrollBarRef.current;
        const timeValue = Math.round((Scrolltop - TIME_CHIP_HEIGHT) / TIME_CHIP_HEIGHT);
        if (Scrolltop === 0) {
            scrollBarRef.current.scroll({
                top: scrollHeight - SCROLL_HEIGHT - TIME_CHIP_HEIGHT,
            });
            return;
        }
        if (Scrolltop + SCROLL_HEIGHT === scrollHeight) {
            scrollBarRef.current.scroll({ top: TIME_CHIP_HEIGHT });
            return;
        }
        if (typeof onValueChange === "function" && /\d/.test(String(timeValue))) {
            let time = timeValue * interval;
            if (isHoursColumn) {
                if (isAnteMeridiem && timeValue === 0) {
                    time = 12;
                }
                else if (timeValue === 24) {
                    time = 0;
                }
            }
            else if (timeValue === 60) {
                time = 0;
            }
            onValueChange(handleLeadingZero(time));
        }
    };
    const handleOnFocusAreaKeyDown = (event) => {
        if (!scrollBarRef.current) {
            return;
        }
        let scrollTop = 0;
        const { scrollHeight } = scrollBarRef.current;
        const currentScrollHeight = scrollHeight - (scrollHeight % 40);
        if (event.key === KeyBoardKeys.Enter) {
            const { nextElementSibling } = focusAreaRef.current;
            if (nextElementSibling) {
                nextElementSibling.focus();
            }
            else if (onCloseRequest && typeof onCloseRequest === "function")
                onCloseRequest();
        }
        if (event.key === KeyBoardKeys.ArrowUp) {
            event.preventDefault();
            if (scrollBarRef.current.scrollTop ===
                currentScrollHeight - SCROLL_HEIGHT) {
                animateScroll(40, 0, () => {
                    animateScroll(80);
                });
            }
            else {
                scrollTop = scrollBarRef.current.scrollTop + TIME_CHIP_HEIGHT;
                animateScroll(scrollTop);
            }
        }
        if (event.key === KeyBoardKeys.ArrowDown) {
            event.preventDefault();
            scrollTop = scrollBarRef.current.scrollTop - TIME_CHIP_HEIGHT;
            if (scrollBarRef.current.scrollTop === 0) {
                scrollTop = currentScrollHeight - 240;
                animateScroll(scrollTop, 0, () => {
                    animateScroll(scrollTop - 40);
                });
            }
            else {
                animateScroll(scrollTop);
            }
        }
        if (event.key === KeyBoardKeys.ArrowLeft) {
            const { previousElementSibling } = focusAreaRef.current;
            if (previousElementSibling)
                previousElementSibling.focus();
        }
        if (event.key === KeyBoardKeys.ArrowRight) {
            const { nextElementSibling } = focusAreaRef.current;
            if (nextElementSibling)
                nextElementSibling.focus();
        }
    };
    function setUpFocusArea() {
        return () => {
            const focusAreas = document.querySelectorAll(".focus-area");
            const focusArea = focusAreas[scrollBarIndex];
            focusAreaRef.current = focusArea;
        };
    }
    function setUpEventsListener() {
        return () => {
            scrollBarRef.current.addEventListener("keydown", preventScroll);
            if (focusAreaRef.current) {
                focusAreaRef.current.addEventListener("keydown", handleOnFocusAreaKeyDown);
            }
        };
    }
    useEffect(setUpFocusArea(), []);
    useEffect(setUpEventsListener(), [focusAreaRef.current]);
    return (_jsx("div", Object.assign({ ref: scrollBarRef, onScroll: debounce(onScroll, ANIMATION_DURATION + SCROLL_DELAY) }, { children: _jsx("div", { children: chips }) })));
}
